import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, AlertCircle, Mail } from "lucide-react";
import { adminLogin } from "@/lib/adminApi";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const ok = await adminLogin(email.trim(), password);
      if (ok) {
        toast.success("Welcome back");
        const next = searchParams.get("next");
        const safe = next && next.startsWith("/adminpanel") ? next : "/adminpanel";
        navigate(safe, { replace: true });
      } else {
        setErr("Invalid email or password — please try again.");
        toast.error("Invalid credentials");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Sign-in failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#071A33] flex items-center justify-center p-6 relative overflow-hidden" data-testid="admin-login-page">
      <Helmet>
        <title>Admin Sign-in | Park Home Support</title>
        <meta name="robots" content="noindex,nofollow"/>
      </Helmet>
      <img
        src="/images/logo-dark.png"
        alt=""
        aria-hidden="true"
        className="absolute -right-20 -bottom-20 w-[640px] opacity-[0.04] pointer-events-none select-none"
      />
      <div className="absolute -left-32 -top-32 w-[420px] h-[420px] bg-[#0F766E]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute right-1/3 top-10 w-[280px] h-[280px] bg-[#B88A2A]/10 rounded-full blur-3xl pointer-events-none" />

      <form onSubmit={onSubmit} className="relative w-full max-w-md bg-white p-10 border-t-4 border-[#B88A2A] shadow-2xl">
        <img
          src="/images/logo-light.png"
          alt="Park Home Support"
          className="h-16 w-auto -ml-2 mb-6"
        />
        <div className="flex items-center gap-2 mb-2">
          <Lock size={16} className="text-[#064E2E]"/>
          <span className="eyebrow">Operations Console</span>
        </div>
        <h1 className="font-display text-3xl text-[#071A33] leading-tight">Welcome back.</h1>
        <p className="mt-2 text-sm text-[#475569]">Sign in with your operator email and password.</p>

        <label className="field-label mt-6" htmlFor="admin-email">Email</label>
        <div className="relative">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"/>
          <input
            id="admin-email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            className="field-input pl-9"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (err) setErr(""); }}
            data-testid="admin-email-input"
          />
        </div>

        <label className="field-label mt-4" htmlFor="admin-password">Password</label>
        <input
          id="admin-password"
          type="password"
          autoComplete="current-password"
          required
          className="field-input"
          value={password}
          onChange={(e) => { setPassword(e.target.value); if (err) setErr(""); }}
          data-testid="admin-password-input"
        />

        {err && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-[#DC2626]/5 border-l-4 border-[#DC2626] text-sm text-[#DC2626]" data-testid="admin-login-error" role="alert">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <button disabled={busy} className="btn-primary mt-5 w-full" data-testid="admin-login-btn">
          {busy ? <><Loader2 className="animate-spin" size={16}/> Signing in…</> : "Sign in"}
        </button>

        <p className="mt-6 text-[11px] tracking-[0.18em] uppercase text-[#94A3B8] text-center">
          Park Home Support · Internal access
        </p>
      </form>
    </div>
  );
}
